import { Router } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { uptime } from '@/lib/freebox'
import {
  portLabel,
  portTempTone,
  type MikrotikDevice,
  type MikrotikPort,
  type MikrotikTempThresholds,
} from '@/lib/mikrotik'

const TONE_JACK: Record<string, string> = {
  ok: 'border-emerald-500/60 bg-emerald-500/10 shadow-[0_0_8px] shadow-emerald-500/30',
  warn: 'border-amber-500/60 bg-amber-500/10 shadow-[0_0_8px] shadow-amber-500/40',
  crit: 'border-destructive/70 bg-destructive/10 shadow-[0_0_8px] shadow-destructive/50',
  none: 'border-border bg-muted/40',
}
const TONE_TEXT: Record<string, string> = {
  ok: 'text-emerald-500',
  warn: 'text-amber-500 font-semibold',
  crit: 'text-destructive font-semibold',
  none: 'text-muted-foreground',
}

function mbps(v: number | null): string {
  return v == null ? '—' : `${((v * 8) / 1e6).toFixed(1)} Mb/s`
}

function PortSlot({
  p,
  thresholds,
}: {
  p: MikrotikPort
  thresholds: MikrotikTempThresholds
}) {
  const tone = p.link ? portTempTone(p.sfp_temp_c, thresholds) : 'none'
  const jackTone = p.link && tone === 'none' ? 'ok' : tone
  return (
    <div
      className="w-[84px] shrink-0 rounded-md border border-border bg-card px-1.5 py-1.5 text-center"
      title={[
        p.sfp_vendor && `${p.sfp_vendor} ${p.sfp_model ?? ''}`,
        p.sfp_rx_dbm != null && `Rx ${p.sfp_rx_dbm.toFixed(1)} dBm / Tx ${p.sfp_tx_dbm?.toFixed(1) ?? '—'} dBm`,
        p.sfp_temp_max_24h != null && `max 24h ${p.sfp_temp_max_24h.toFixed(0)}°C`,
        p.link && `↓ ${mbps(p.rate_rx_bytes_s)} · ↑ ${mbps(p.rate_tx_bytes_s)}`,
        p.neighbor?.ip ?? undefined,
      ]
        .filter(Boolean)
        .join('\n')}
    >
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">
        {portLabel(p.port)}
      </div>
      <div
        className={`relative mx-auto my-1 h-4 w-9 rounded-sm border ${TONE_JACK[jackTone]}`}
      >
        <span
          className={`absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full ${
            p.link ? 'bg-emerald-500' : 'bg-muted-foreground/40'
          }`}
        />
      </div>
      <div className="truncate text-[10px] font-semibold">
        {p.neighbor?.name ?? p.neighbor?.ip ?? (p.link ? '?' : '—')}
      </div>
      <div className="text-[9px] text-muted-foreground">
        {p.link ? (p.speed ?? 'up') : 'libre'}
      </div>
      {p.sfp_temp_c != null && (
        <div className={`text-[9px] tabular-nums ${TONE_TEXT[tone]}`}>
          {p.sfp_temp_c.toFixed(0)} °C{tone === 'warn' || tone === 'crit' ? ' ⚠' : ''}
        </div>
      )}
    </div>
  )
}

export function DeviceFaceplate({
  device,
  thresholds,
  highlighted = false,
  id,
}: {
  device: MikrotikDevice
  thresholds: MikrotikTempThresholds
  highlighted?: boolean
  id?: string
}) {
  const sortedPorts = [...device.ports].sort((a, b) =>
    a.port.localeCompare(b.port, undefined, { numeric: true }),
  )
  return (
    <Card
      id={id}
      className={`${highlighted ? 'ring-2 ring-primary' : ''} ${
        device.reachable ? '' : 'opacity-60'
      }`}
    >
      <CardHeader className="flex-row items-baseline justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Router className="h-4 w-4 text-primary" />
          {device.board_name ?? device.device}
          <span className="text-xs font-normal text-muted-foreground">
            {device.device}
          </span>
        </CardTitle>
        <span className="text-xs text-muted-foreground tabular-nums">
          {device.reachable ? (
            <>
              RouterOS {device.version ?? '—'} · up {uptime(device.uptime_s)} · CPU{' '}
              {device.cpu_pct != null ? `${device.cpu_pct.toFixed(0)}%` : '—'} · RAM{' '}
              {device.mem_used_pct != null ? `${device.mem_used_pct.toFixed(0)}%` : '—'}
              {device.temp_board_c != null && <> · carte {device.temp_board_c.toFixed(0)} °C</>}
            </>
          ) : (
            <span className="text-destructive">
              injoignable depuis {Math.round(device.age_s / 60)} min
            </span>
          )}
        </span>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sortedPorts.map((p) => (
            <PortSlot key={p.port} p={p} thresholds={thresholds} />
          ))}
          {sortedPorts.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Pas encore de données de ports.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
